import { CalibrationEngine } from "@studystraight/calibration-core";
import { calibrationMessages, privacyNotice, resolveConfig } from "@studystraight/shared-config";
import type {
  CalibrationSnapshot,
  CameraPlacement,
  PostureBaseline,
  VisionResult,
} from "@studystraight/shared-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AppSettings } from "../../app/settings.js";
import { useCamera } from "../../hooks/useCamera.js";
import { useVisionTracking } from "../../hooks/useVisionTracking.js";
import { CameraPreview } from "../../ui/CameraPreview.js";
import { SkeletonCanvas } from "../debug/SkeletonCanvas.js";
import { useDebugRecorder } from "../debug/useDebugRecorder.js";

const IDLE: CalibrationSnapshot = {
  phase: "IDLE",
  remainingMs: 0,
  samplesCollected: 0,
  acceptedRatio: 0,
};

export interface CalibrationScreenProps {
  placement: CameraPlacement;
  settings: AppSettings;
  onDone: (baseline: PostureBaseline) => void;
  onBack: () => void;
}

function hintFor(snapshot: CalibrationSnapshot): string {
  switch (snapshot.phase) {
    case "IDLE":
      return calibrationMessages.intro;
    case "COUNTDOWN":
      return calibrationMessages.countdown;
    case "COLLECTING":
      return calibrationMessages.collecting;
    case "DONE":
      return calibrationMessages.done;
    case "FAILED":
      return calibrationMessages[snapshot.failureReason ?? "NOT_ENOUGH_SAMPLES"];
  }
}

/**
 * §11 — hiệu chỉnh baseline.
 *
 * Toàn bộ luật thời gian nằm trong `CalibrationEngine` (không `setTimeout` ở đây): engine tự
 * chuyển phase theo `vision.timestamp`, nên nếu trình duyệt throttle tab thì đợt calibrate chậm
 * lại chứ không kết thúc sớm với 2 giây dữ liệu.
 */
export function CalibrationScreen({
  placement,
  settings,
  onDone,
  onBack,
}: CalibrationScreenProps): React.JSX.Element {
  // Camera đã chọn ở Camera Setup phải theo sang đây: baseline được đo bằng đúng camera mà lát
  // nữa Monitoring dùng, nếu lệch thì khung hình khác góc và mọi delta so với baseline đều sai.
  const camera = useCamera({ facingMode: settings.facingMode });
  const cameraLive = camera.status === "live";
  const recorder = useDebugRecorder();
  const [snapshot, setSnapshot] = useState<CalibrationSnapshot>(IDLE);

  const config = useMemo(
    () => resolveConfig(placement, settings.overrides[placement]),
    [placement, settings.overrides],
  );
  const engine = useMemo(() => new CalibrationEngine({ config }), [config]);

  const started = useRef(false);
  const latest = useRef<CalibrationSnapshot>(IDLE);

  const onFrame = useCallback(
    (vision: VisionResult, inferenceMs: number) => {
      recorder.push(vision, inferenceMs);

      // Bắt đầu ở frame ĐẦU TIÊN, không ở lúc mount: `startedAt` phải cùng hệ quy chiếu với
      // `vision.timestamp` (media time của <video>), và lúc mount thì chưa có frame nào để lấy mốc.
      if (!started.current) {
        engine.start(vision.timestamp);
        started.current = true;
      }

      const next = engine.push(vision);
      const prev = latest.current;
      // Chỉ re-render khi phase đổi hoặc số giây hiển thị đổi (~1 lần/giây) thay vì mỗi frame:
      // §31 giới hạn UI ở 2–5 FPS, và đồng hồ đếm ngược không cần mịn hơn một giây.
      const secondChanged =
        Math.ceil(prev.remainingMs / 1000) !== Math.ceil(next.remainingMs / 1000);
      if (prev.phase !== next.phase || secondChanged) {
        latest.current = next;
        setSnapshot(next);
      }
    },
    [engine, recorder],
  );

  const vision = useVisionTracking({
    placement,
    videoRef: camera.videoRef,
    enabled: cameraLive,
    onFrame,
  });

  const startCamera = camera.start;
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  // `onDone` đi qua ref: nó điều hướng sang màn Monitoring, và nếu nằm trong dependency của effect
  // thì một `onDone` mới (parent re-render) sẽ gọi lại điều hướng lần thứ hai.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    if (snapshot.phase === "DONE" && snapshot.baseline) onDoneRef.current(snapshot.baseline);
  }, [snapshot]);

  const retry = useCallback(() => {
    engine.reset();
    started.current = false;
    latest.current = IDLE;
    setSnapshot(IDLE);
  }, [engine]);

  const countdown =
    snapshot.phase === "COUNTDOWN" || snapshot.phase === "COLLECTING"
      ? Math.ceil(snapshot.remainingMs / 1000)
      : null;

  return (
    <main className="screen">
      <header className="topbar">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          ← Quay lại
        </button>
        <h2 className="topbar__title">Hiệu chỉnh tư thế</h2>
      </header>

      <div className="setup__body">
        <div className="stage">
          <CameraPreview camera={camera}>
            {settings.debugMode ? (
              <SkeletonCanvas
                getVision={recorder.latestVision}
                minVisibility={config.posture.minLandmarkVisibility}
                mirrored={camera.facingMode === "user"}
              />
            ) : null}
          </CameraPreview>
        </div>

        <div className="setup__panel">
          <p className="calibration__hint">{hintFor(snapshot)}</p>

          {countdown === null ? null : (
            <p className="calibration__count" aria-live="polite">
              {countdown}
            </p>
          )}

          {snapshot.phase === "FAILED" ? (
            <div className="actions">
              <button type="button" className="btn btn--secondary" onClick={onBack}>
                Đổi vị trí máy
              </button>
              <button type="button" className="btn btn--primary" onClick={retry}>
                Thử lại
              </button>
            </div>
          ) : null}

          {!vision.ready ? <p className="hint">Đang tải bộ nhận diện tư thế…</p> : null}
          <p className="privacy">{privacyNotice}</p>
        </div>
      </div>
    </main>
  );
}
